import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as email from '../email';
import { IMeyMessageInboxClient, MessageInboxItem, MessageOutboxItem, MeyMessageInboxClient, EmailClassificationCategory, EmailClassificationClient, EmailClassification, PaginatedInboxResult } from '../../../web-api-client';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ReadMailComponent } from '../read-mail/read-mail.component';
import { forkJoin } from 'rxjs';
import { MailFilterNavigation } from '../../../shared/enum/mail-filter-navigation.enum';
import { MailSortOption } from '../../../shared/enum/mail-sort-option.enum';
import { ReadOutBoxComponent } from '../read-outbox/read-outbox.component';
import { MailClassificationFilter } from '../../../shared/enum/mail-classification-filter.enum';
import { WtStorageService } from '../../../wt-storage.service';
import { ChangeClassificationModalComponent } from '../change-classification-modal/change-classification-modal.component';
import { FolderManagementModalComponent, MailFolderWithCount } from '../folder-management-modal/folder-management-modal.component';
import { MoveToFolderModalComponent } from '../move-to-folder-modal/move-to-folder-modal.component';
import { MailFolderItem } from '../email-filter/email-filter.component';
import { ToastrService } from 'ngx-toastr';
import { ConfirmModalComponent } from '../../../modals/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-mail-inbox',
  templateUrl: './mail-inbox.component.html',
  styleUrls: ['./mail-inbox.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush
})
export class MailInboxComponent implements OnInit {

  public isLoaded = false;
  show = false;
  showSortMenu = false;

  public emails: any[] = [];
  public inboxEmails: MessageInboxItem[] = [];
  public outBoxItems: MessageOutboxItem[] = [];

  // data
  public emailData = email.emailData;
  public selectedNavigation = MailFilterNavigation.Inbox;
  public mailFilterNavigation = MailFilterNavigation;

  // Sorting
  public currentSortOption: MailSortOption = MailSortOption.DateNewest;
  public mailSortOption = MailSortOption;
  private readonly SORT_STORAGE_KEY = 'mail-inbox-sort-option';

  // Global classification filter
  public currentClassificationFilter: MailClassificationFilter = MailClassificationFilter.All;
  public MailClassificationFilter = MailClassificationFilter; // Expose enum to template
  private readonly CLASSIFICATION_FILTER_STORAGE_KEY = 'mailbox_classification_filter';

  // Classification state tracking
  public classifyingEmailIds: Set<number> = new Set<number>();
  public classificationErrors: Map<number, string> = new Map<number, string>();

  // Custom folders
  public customFolders: MailFolderItem[] = [];
  public selectedFolderId: number | null = null;

  // Multi-select mode
  public isMultiSelectMode: boolean = false;
  public selectedMessageIds: Set<number> = new Set<number>();

  // Pagination state
  public currentPage: number = 1;
  public pageSize: number = 20;
  public totalCount: number = 0;
  public totalPages: number = 0;
  public pageSizeOptions: number[] = [20, 60, 100];
  private readonly PAGE_SIZE_STORAGE_KEY = 'mailInbox_pageSize';

  // Total counts for sidebar (from backend, across all pages)
  public totalUnreadCount: number = 0;
  public totalReadCount: number = 0;

  private readonly mailFolderApiUrl = '/api/MailFolder';
  private readonly meyMessageInboxApiUrl = '/api/MeyMessageInbox';

  constructor(
    public client: MeyMessageInboxClient,
    public changeDetect: ChangeDetectorRef,
    private readonly modalService: NgbModal,
    private readonly storageService: WtStorageService,
    private readonly classificationClient: EmailClassificationClient,
    private readonly toastr: ToastrService,
    private readonly http: HttpClient
  ) {
    // Load saved sort preference from localStorage
    const savedSort = localStorage.getItem(this.SORT_STORAGE_KEY);
    if (savedSort !== null) {
      this.currentSortOption = parseInt(savedSort, 10);
    }

    // Load classification filter preference from storage
    const savedFilter = this.storageService.getItem(this.CLASSIFICATION_FILTER_STORAGE_KEY);
    if (savedFilter !== null) {
      this.currentClassificationFilter = parseInt(savedFilter, 10) as MailClassificationFilter;
    }

    // Load page size preference from localStorage
    const savedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);
    if (savedPageSize !== null) {
      const parsedPageSize = parseInt(savedPageSize, 10);
      if (this.pageSizeOptions.includes(parsedPageSize)) {
        this.pageSize = parsedPageSize;
      }
    }

    this.loadData();
    this.loadFolders();
  }

  // menu show
  openMenu() {
    this.show = !this.show;
  }

  // sort menu show
  openSortMenu() {
    this.showSortMenu = !this.showSortMenu;
  }

  ngOnInit(): void { }

  loadData() {
    this.isLoaded = false;
    forkJoin([
      this.client.getPaginated(this.currentPage, this.pageSize),
      this.client.getOutBoxList()
    ]).subscribe(([paginatedResult, outbox]) => {
      this.inboxEmails = paginatedResult.items || [];
      this.totalCount = paginatedResult.totalCount || 0;
      this.totalPages = paginatedResult.totalPages || 0;
      this.totalUnreadCount = paginatedResult.unreadCount || 0;
      this.totalReadCount = paginatedResult.readCount || 0;
      this.outBoxItems = outbox;

      // Apply current navigation filter
      this.applyNavigationFilter();

      // Apply sorting
      this.applySorting();

      this.isLoaded = true;
      this.changeDetect.detectChanges();
    })
  }


  formatDate(date: Date | string | null | undefined): string {
    if (!date) {
      return '';
    }

    try {
      const dateObj = new Date(date);

      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        return '';
      }

      const day = dateObj.getDate().toString().padStart(2, '0');
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
      const year = dateObj.getFullYear();

      return `${day}.${month}.${year}`;
    } catch (error) {
      console.warn('Error formatting date:', date, error);
      return '';
    }
  }

  public openDetails(mail: MessageInboxItem|MessageOutboxItem): void {


    if (this.selectedNavigation == MailFilterNavigation.Inbox) {
      let modalRef = this.modalService.open(ReadMailComponent, { size: 'xl', centered: true, backdrop: 'static' });
      modalRef.componentInstance.message = mail;
      modalRef.componentInstance.onClose.subscribe(r => {

        this.loadData();
      });
    }
    else {
      let modalRef = this.modalService.open(ReadOutBoxComponent, { size: 'xl', centered: true, backdrop: 'static' });
      modalRef.componentInstance.message = mail;
      modalRef.componentInstance.onClose.subscribe(r => {

        this.loadData();
      });
    }

  }

  public unReadCount() {
    return this.inboxEmails.filter(d => !d.isRead).length;
  }

  public readCount() {
    return this.inboxEmails.filter(d => d.isRead).length;
  }

  public starredCount() {
    return this.inboxEmails.filter(d => d.liked).length;
  }

  public starredApplicationsCount() {
    return this.inboxEmails.filter(d => {
      if (!d.liked) return false;
      const classification = this.getLatestClassification(d);
      return classification && classification.category === 1; // Zayavlenie
    }).length;
  }

  public starredOthersCount() {
    return this.inboxEmails.filter(d => {
      if (!d.liked) return false;
      const classification = this.getLatestClassification(d);
      return !classification || classification.category !== 1;
    }).length;
  }

  public setStar(mail: MessageInboxItem) {
    // Toggle the liked property FIRST
    mail.liked = !mail.liked;

    // Then send the updated value to the backend
    this.client.update(mail).subscribe(d => {
      if (d) {
        // Successfully updated
        this.changeDetect.detectChanges();
      } else {
        // If update failed, revert the change
        mail.liked = !mail.liked;
        this.changeDetect.detectChanges();
      }
    }, error => {
      // If error occurred, revert the change
      console.error('Error updating starred status:', error);
      mail.liked = !mail.liked;
      this.changeDetect.detectChanges();
    });
  }

  public onNavigate(event: MailFilterNavigation): void {
    this.selectedNavigation = event;
    this.currentPage = 1; // Reset to first page when navigation changes
    this.applyNavigationFilter();
    this.applySorting();
    this.changeDetect.detectChanges();
  }

  private applyNavigationFilter(): void {
    // First, apply navigation filter
    let filteredEmails: MessageInboxItem[] = [];

    switch (this.selectedNavigation) {
      case MailFilterNavigation.Inbox: {
        filteredEmails = [...this.inboxEmails];
        break;
      }
      case MailFilterNavigation.OutBox: {
        this.emails = [...this.outBoxItems];
        return; // OutBox doesn't need classification filtering
      }
      case MailFilterNavigation.Starred: {
        filteredEmails = this.inboxEmails.filter(r => r.liked);
        break;
      }
      case MailFilterNavigation.StarredApplications: {
        filteredEmails = this.inboxEmails.filter(r => {
          if (!r.liked) return false;
          const classification = this.getLatestClassification(r);
          return classification && classification.category === 1; // Zayavlenie
        });
        break;
      }
      case MailFilterNavigation.StarredOthers: {
        filteredEmails = this.inboxEmails.filter(r => {
          if (!r.liked) return false;
          const classification = this.getLatestClassification(r);
          return !classification || classification.category !== 1;
        });
        break;
      }
      case MailFilterNavigation.UnRead: {
        filteredEmails = this.inboxEmails.filter(r => !r.isRead);
        break;
      }
      case MailFilterNavigation.Read: {
        filteredEmails = this.inboxEmails.filter(r => r.isRead);
        break;
      }
      case MailFilterNavigation.CustomFolder: {
        // Filter by selected folder ID
        if (this.selectedFolderId !== null) {
          filteredEmails = this.inboxEmails.filter(r => (r as any).idMailFolder === this.selectedFolderId);
        } else {
          filteredEmails = [];
        }
        break;
      }
      default: {
        filteredEmails = [...this.inboxEmails];
        break;
      }
    }

    // Then, apply global classification filter
    this.emails = this.applyClassificationFilter(filteredEmails);
  }

  /**
   * Get the latest classification for a message
   * Returns the most recent classification from the emailClassifications array
   */
  private getLatestClassification(message: MessageInboxItem): EmailClassification | undefined {
    if (!message.emailClassifications || message.emailClassifications.length === 0) {
      return undefined;
    }

    // Return the most recent classification (sorted by classifiedAt descending)
    return message.emailClassifications
      .sort((a, b) => {
        const dateA = a.classifiedAt ? new Date(a.classifiedAt).getTime() : 0;
        const dateB = b.classifiedAt ? new Date(b.classifiedAt).getTime() : 0;
        return dateB - dateA;
      })[0];
  }

  /**
   * Apply global classification filter to a list of messages
   */
  private applyClassificationFilter(messages: MessageInboxItem[]): MessageInboxItem[] {
    switch (this.currentClassificationFilter) {
      case MailClassificationFilter.All:
        return messages;

      case MailClassificationFilter.Zayavlenie:
        return messages.filter(m => {
          const classification = this.getLatestClassification(m);
          return classification && classification.category === EmailClassificationCategory.Zayavlenie;
        });

      case MailClassificationFilter.Other:
        return messages.filter(m => {
          const classification = this.getLatestClassification(m);
          return classification && classification.category === EmailClassificationCategory.Other;
        });

      default:
        return messages;
    }
  }

  /**
   * Handle classification filter change
   */
  public onClassificationFilterChange(filter: MailClassificationFilter): void {
    this.currentClassificationFilter = filter;
    this.currentPage = 1; // Reset to first page when classification filter changes

    // Save to storage
    this.storageService.setItem(this.CLASSIFICATION_FILTER_STORAGE_KEY, filter.toString());

    // Reapply filters
    this.applyNavigationFilter();
    this.applySorting();
    this.changeDetect.detectChanges();
  }

  public onSortChange(sortOption: MailSortOption): void {
    this.currentSortOption = sortOption;
    // Save to localStorage
    localStorage.setItem(this.SORT_STORAGE_KEY, sortOption.toString());
    this.currentPage = 1; // Reset to first page when sort changes
    this.applySorting();
    this.showSortMenu = false;
    this.changeDetect.detectChanges();
  }

  // ==================== PAGINATION ====================

  /**
   * Handle page change
   */
  public onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.clearSelection(); // Clear multi-select on page change
    this.loadData();
  }

  /**
   * Handle page size change
   */
  public onPageSizeChange(newPageSize: number): void {
    if (!this.pageSizeOptions.includes(newPageSize) || newPageSize === this.pageSize) {
      return;
    }
    this.pageSize = newPageSize;
    this.currentPage = 1; // Reset to first page when page size changes
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, newPageSize.toString());
    this.loadData();
  }

  /**
   * Get array of page numbers for pagination display
   * Shows max 5 page numbers with current page in the middle when possible
   */
  public getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    // Adjust startPage if endPage is at the maximum
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  /**
   * Calculate the starting item number for current page (1-based)
   */
  public getStartItem(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  /**
   * Calculate the ending item number for current page
   */
  public getEndItem(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  private applySorting(): void {
    if (!this.emails || this.emails.length === 0) {
      return;
    }

    switch (this.currentSortOption) {
      case MailSortOption.DateNewest:
        this.emails.sort((a, b) => {
          const dateA = a.dateSent ? new Date(a.dateSent).getTime() : 0;
          const dateB = b.dateSent ? new Date(b.dateSent).getTime() : 0;
          return dateB - dateA; // Newest first
        });
        break;

      case MailSortOption.DateOldest:
        this.emails.sort((a, b) => {
          const dateA = a.dateSent ? new Date(a.dateSent).getTime() : 0;
          const dateB = b.dateSent ? new Date(b.dateSent).getTime() : 0;
          return dateA - dateB; // Oldest first
        });
        break;

      case MailSortOption.SenderAZ:
        this.emails.sort((a, b) => {
          const nameA = this.getSenderName(a).toLowerCase();
          const nameB = this.getSenderName(b).toLowerCase();
          return nameA.localeCompare(nameB, 'bg'); // A-Z with Bulgarian locale
        });
        break;

      case MailSortOption.SenderZA:
        this.emails.sort((a, b) => {
          const nameA = this.getSenderName(a).toLowerCase();
          const nameB = this.getSenderName(b).toLowerCase();
          return nameB.localeCompare(nameA, 'bg'); // Z-A with Bulgarian locale
        });
        break;

      case MailSortOption.SubjectAZ:
        this.emails.sort((a, b) => {
          const subjectA = (a.subject || '').toLowerCase();
          const subjectB = (b.subject || '').toLowerCase();
          return subjectA.localeCompare(subjectB, 'bg'); // A-Z with Bulgarian locale
        });
        break;

      case MailSortOption.SubjectZA:
        this.emails.sort((a, b) => {
          const subjectA = (a.subject || '').toLowerCase();
          const subjectB = (b.subject || '').toLowerCase();
          return subjectB.localeCompare(subjectA, 'bg'); // Z-A with Bulgarian locale
        });
        break;
    }
  }

  private getSenderName(email: MessageInboxItem | MessageOutboxItem): string {
    if ('senderProfileName' in email) {
      return email.senderProfileName || 'Неизвестен подател';
    } else if ('recipients' in email) {
      return email.recipients || 'Неизвестен получател';
    }
    return '';
  }

  public getSortLabel(sortOption: MailSortOption): string {
    switch (sortOption) {
      case MailSortOption.DateNewest:
        return 'Дата: Най-нови първо';
      case MailSortOption.DateOldest:
        return 'Дата: Най-стари първо';
      case MailSortOption.SenderAZ:
        return 'Подател: А-Я';
      case MailSortOption.SenderZA:
        return 'Подател: Я-А';
      case MailSortOption.SubjectAZ:
        return 'Тема: А-Я';
      case MailSortOption.SubjectZA:
        return 'Тема: Я-А';
      default:
        return 'Дата: Най-нови първо';
    }
  }

  /**
   * Get the classification label for display in the badge
   */
  public getClassificationLabel(email: MessageInboxItem): string {
    const classification = this.getLatestClassification(email);
    if (!classification) {
      return 'Некласифицирано';
    }

    switch (classification.category) {
      case EmailClassificationCategory.Zayavlenie:
        return 'Заявление';
      case EmailClassificationCategory.Other:
        return 'Други';
      case EmailClassificationCategory.Unknown:
      default:
        return 'Некласифицирано';
    }
  }

  /**
   * Get the CSS class for the classification badge based on category
   */
  public getClassificationBadgeClass(email: MessageInboxItem): string {
    const classification = this.getLatestClassification(email);
    if (!classification) {
      return 'badge-unknown';
    }

    switch (classification.category) {
      case EmailClassificationCategory.Zayavlenie:
        return 'badge-zayavlenie';
      case EmailClassificationCategory.Other:
        return 'badge-other';
      case EmailClassificationCategory.Unknown:
      default:
        return 'badge-unknown';
    }
  }

  /**
   * Check if an email is currently being classified
   */
  public isClassifying(email: MessageInboxItem): boolean {
    return this.classifyingEmailIds.has(email.id!);
  }

  /**
   * Get classification error message for an email
   */
  public getClassificationError(email: MessageInboxItem): string | undefined {
    return this.classificationErrors.get(email.id!);
  }

  /**
   * Classify an unclassified email using the existing classification API
   */
  public classifyEmail(email: MessageInboxItem): void {
    // Prevent multiple simultaneous classification requests for the same email
    if (this.isClassifying(email)) {
      return;
    }

    // Clear any previous error
    this.classificationErrors.delete(email.id!);

    // Mark as classifying
    this.classifyingEmailIds.add(email.id!);
    this.changeDetect.detectChanges();

    // Call the existing classification API
    this.classificationClient.classifyEmail(
      email.messageId,
      true, // saveToDatabase
      0.5,  // minimumConfidenceThreshold (default)
      '1.0' // modelVersion (default)
    ).subscribe({
      next: (response) => {
        // Update the email object with the classification result
        if (response.result && response.result.success) {
          // Create new classification object
          const newClassification: EmailClassification = {
            messageInboxItemId: email.id,
            category: response.result.category,
            confidenceScore: response.result.confidenceScore,
            classificationReason: response.result.classificationReason,
            classifiedAt: response.result.classifiedAt,
            hasZayavlenieAttachment: response.result.hasZayavlenieAttachment,
            detectedFormCodes: response.result.detectedFormCodes,
            attachmentCount: response.result.attachmentCount,
            isManualClassification: false,
            processingDurationMs: response.result.processingDurationMs
          } as any;

          // Add to emailClassifications array (or create array if it doesn't exist)
          if (!email.emailClassifications) {
            email.emailClassifications = [];
          }
          email.emailClassifications.push(newClassification);

          // Remove from classifying set
          this.classifyingEmailIds.delete(email.id!);
          this.changeDetect.detectChanges();
        } else {
          // Classification failed
          this.classifyingEmailIds.delete(email.id!);
          this.classificationErrors.set(
            email.id!,
            response.result?.errorMessage || 'Неуспешна класификация'
          );
          this.changeDetect.detectChanges();
        }
      },
      error: (error) => {
        console.error('Failed to classify email:', error);
        this.classifyingEmailIds.delete(email.id!);
        this.classificationErrors.set(
          email.id!,
          'Грешка при класификация. Моля, опитайте отново.'
        );
        this.changeDetect.detectChanges();
      }
    });
  }

  /**
   * Open the change classification modal for an email
   */
  public openChangeClassificationModal(email: MessageInboxItem): void {
    const modalRef = this.modalService.open(ChangeClassificationModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static'
    });

    modalRef.componentInstance.message = email;

    modalRef.componentInstance.onClose.subscribe((success: boolean) => {
      if (success) {
        // Show success toaster notification
        this.toastr.success('Класификацията е променена успешно', 'Успех', {
          timeOut: 3000,
          closeButton: true,
          positionClass: 'toast-top-right'
        });

        // Refresh the email list to reflect the change
        this.changeDetect.detectChanges();
      }
    });
  }

  // ==================== FOLDER MANAGEMENT ====================

  /**
   * Load custom folders from API
   */
  public loadFolders(): void {
    this.http.get<MailFolderWithCount[]>(`${this.mailFolderApiUrl}/all`).subscribe({
      next: (folders) => {
        this.customFolders = folders.map(f => ({
          idMailFolder: f.idMailFolder,
          name: f.name,
          messageCount: f.messageCount
        }));
        this.changeDetect.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load folders:', error);
      }
    });
  }

  /**
   * Navigate to a custom folder
   */
  public onNavigateToFolder(folderId: number): void {
    this.selectedFolderId = folderId;
    this.selectedNavigation = MailFilterNavigation.CustomFolder;
    this.clearSelection();
    this.applyNavigationFilter();
    this.applySorting();
    this.changeDetect.markForCheck();
  }

  /**
   * Open the folder management modal
   */
  public openFolderManagementModal(): void {
    const modalRef = this.modalService.open(FolderManagementModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static'
    });

    modalRef.componentInstance.onClose.subscribe(() => {
      // Reload folders after modal closes
      this.loadFolders();
      this.loadData();
    });
  }

  // ==================== MULTI-SELECT MODE ====================

  /**
   * Toggle message selection
   */
  public toggleMessageSelection(messageId: number): void {
    if (this.selectedMessageIds.has(messageId)) {
      this.selectedMessageIds.delete(messageId);
    } else {
      this.selectedMessageIds.add(messageId);
    }

    // Enable/disable multi-select mode based on selection count
    this.isMultiSelectMode = this.selectedMessageIds.size > 0;
    this.changeDetect.markForCheck();
  }

  /**
   * Check if a message is selected
   */
  public isMessageSelected(messageId: number): boolean {
    return this.selectedMessageIds.has(messageId);
  }

  /**
   * Select all visible emails
   */
  public selectAll(): void {
    this.emails.forEach(email => {
      if (email.id) {
        this.selectedMessageIds.add(email.id);
      }
    });
    this.isMultiSelectMode = this.selectedMessageIds.size > 0;
    this.changeDetect.markForCheck();
  }

  /**
   * Clear all selections
   */
  public clearSelection(): void {
    this.selectedMessageIds.clear();
    this.isMultiSelectMode = false;
    this.changeDetect.markForCheck();
  }

  /**
   * Move selected messages to a folder
   * @param targetFolderId The folder ID to move to, or null to move to Inbox
   */
  public moveMessagesToFolder(targetFolderId: number | null): void {
    if (this.selectedMessageIds.size === 0) {
      return;
    }

    const messageCount = this.selectedMessageIds.size;
    const folderName = targetFolderId === null
      ? 'Входящи'
      : this.customFolders.find(f => f.idMailFolder === targetFolderId)?.name || 'папка';

    // Show confirmation dialog for multiple messages
    if (messageCount > 1) {
      const confirmModalRef = this.modalService.open(ConfirmModalComponent, {
        centered: true,
        backdrop: 'static'
      });

      confirmModalRef.componentInstance.title = 'Преместване на съобщения';
      confirmModalRef.componentInstance.message = `Сигурни ли сте, че искате да преместите ${messageCount} съобщения в "${folderName}"?`;
      confirmModalRef.componentInstance.confirmText = 'Премести';
      confirmModalRef.componentInstance.cancelText = 'Отказ';
      confirmModalRef.componentInstance.confirmButtonClass = 'btn-primary';

      confirmModalRef.componentInstance.onClose.subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.performMoveMessages(targetFolderId, folderName);
        }
      });
    } else {
      // For single message, move directly without confirmation
      this.performMoveMessages(targetFolderId, folderName);
    }
  }

  /**
   * Perform the actual move operation
   */
  private performMoveMessages(targetFolderId: number | null, folderName: string): void {
    const messageIds = Array.from(this.selectedMessageIds);

    this.http.post<boolean>(`${this.meyMessageInboxApiUrl}/move-to-folder`, {
      messageIds: messageIds,
      targetFolderId: targetFolderId
    }).subscribe({
      next: (success) => {
        if (success) {
          this.toastr.success(
            `${messageIds.length} съобщения са преместени в "${folderName}"`,
            'Успех',
            { timeOut: 3000, closeButton: true }
          );

          this.clearSelection();
          this.loadData();
          this.loadFolders();
        } else {
          this.toastr.error('Неуспешно преместване на съобщенията', 'Грешка');
        }
        this.changeDetect.markForCheck();
      },
      error: (error) => {
        console.error('Failed to move messages:', error);
        this.toastr.error('Грешка при преместване на съобщенията', 'Грешка');
        this.changeDetect.markForCheck();
      }
    });
  }

  /**
   * Open move to folder modal for a single message (from the 3-dots menu)
   */
  public openMoveToFolderModal(email: MessageInboxItem): void {
    if (!email.id) {
      return;
    }

    const modalRef = this.modalService.open(MoveToFolderModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static'
    });

    modalRef.componentInstance.messageIds = [email.id];

    modalRef.componentInstance.onFolderSelected.subscribe((targetFolderId: number | null) => {
      this.moveMessageToFolder(email, targetFolderId);
    });
  }

  /**
   * Move a single message to a folder
   */
  private moveMessageToFolder(email: MessageInboxItem, targetFolderId: number | null): void {
    if (!email.id) {
      return;
    }

    this.http.post<boolean>(`${this.meyMessageInboxApiUrl}/move-to-folder`, {
      messageIds: [email.id],
      targetFolderId: targetFolderId
    }).subscribe({
      next: (success) => {
        if (success) {
          const folderName = targetFolderId === null
            ? 'Входящи'
            : this.customFolders.find(f => f.idMailFolder === targetFolderId)?.name || 'папка';

          this.toastr.success(
            `Съобщението е преместено в "${folderName}"`,
            'Успех',
            { timeOut: 3000, closeButton: true }
          );

          this.loadData();
          this.loadFolders();
        } else {
          this.toastr.error('Неуспешно преместване на съобщението', 'Грешка');
        }
        this.changeDetect.markForCheck();
      },
      error: (error) => {
        console.error('Failed to move message:', error);
        this.toastr.error('Грешка при преместване на съобщението', 'Грешка');
        this.changeDetect.markForCheck();
      }
    });
  }
}
